import { cloneElement } from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import useToggleOpen from '@app/hooks/useToggleOpen'
import Modal from '@app/stories/Modal'
import Heading from '@app/stories/Heading'
import Divider from '@app/stories/Divider'
import NavLink from '@app/stories/NavLink'
import Button from '@app/stories/Button'
import Typography from '@app/stories/Typography'

import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'

/**
 * GeneralModal component renders a modal with customizable header, footer, and close button. It
 * also supports a custom button to trigger the modal.
 *
 * @param {Object} props - Component props.
 * @param {string} [props.buttonText='...'] - Text for the default button that opens the modal.
 *   Default is `'...'`
 * @param {string} [props.headerText='More Info'] - Text for the modal header. Default is `'More
 *   Info'`. Default is `'More Info'`
 * @param {React.ReactNode} [props.customButton=null] - Custom button element to trigger the modal.
 *   Default is `null`
 * @param {string} [props.footerText=''] - Text for the modal footer. Default is `''`
 * @param {React.ReactNode} [props.customFooter=null] - Custom footer element for the modal. Default
 *   is `null`
 * @param {Function} [props.customClose=null] - Custom close button renderer function. Default is
 *   `null`
 * @param {boolean} [props.allowClickAway=false] - Whether clicking outside the modal closes it.
 *   Default is `false`
 * @param {string} [props.maxWidth] - Maximum width of the modal.
 * @param {React.ReactNode} props.children - Content to display inside the modal.
 * @param {Function} [props.onOpen] - Callback function triggered when the modal is opened.
 */
const GeneralModal = ({
  buttonText = '...',
  headerText = 'More Info',
  customButton = null,
  footerText = '',
  customFooter = null,
  customClose = null,
  allowClickAway = false,
  maxWidth,
  children,
  onOpen,
}) => {
  const [anchorel, handleClick, handleClose] = useToggleOpen(allowClickAway)
  const open = Boolean(anchorel)

  const handleOpen = (event) => {
    handleClick(event)
    if (onOpen) {
      onOpen()
    }
  }

  const addOnClickToCustomButton = (btn) => {
    return cloneElement(btn, {
      onClick: handleOpen, // Change this line
    })
  }

  return (
    <>
      {customButton ? (
        addOnClickToCustomButton(customButton)
      ) : (
        <NavLink type='button' onClick={handleOpen} style={{ textDecoration: 'underline' }}>
          {buttonText}
        </NavLink>
      )}
      <Modal maxWidth={maxWidth || 'sm'} open={open} anchorel={anchorel} onClose={handleClose}>
        <Box>
          <Heading variant='h3' align='center'>
            {headerText}
          </Heading>
          <Divider spacing='vertical' />
          {children}
        </Box>
        <Box mt={4}>
          {customFooter || (
            <Typography variant='body2' align='center'>
              {footerText}
            </Typography>
          )}
        </Box>
        {customClose ? (
          customClose(handleClose)
        ) : (
          <Box mt={4}>
            <Button size='medium' variant='contained' onClick={handleClose}>
              <Typography variant='body2' style={{ marginLeft: '8px', marginRight: '8px' }}>
                close
              </Typography>
            </Button>
          </Box>
        )}
      </Modal>
    </>
  )
}

GeneralModal.propTypes = {
  buttonText: PropTypes.string,
  headerText: PropTypes.string,
  customButton: PropTypes.node,
  customFooter: PropTypes.node,
  footerText: PropTypes.string,
  children: PropTypes.node,
  onOpen: PropTypes.func, // Add this line
}

/**
 * GeneralModalNew component renders a modal using Material-UI's Dialog component. It supports a
 * title, content, and primary/secondary actions.
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.open - Whether the modal is open.
 * @param {Function} props.handleClose - Callback function to close the modal.
 * @param {string} [props.title] - Title text for the modal.
 * @param {string} [props.content] - Content text for the modal.
 * @param {Object} props.primaryAction - Primary action for the modal.
 * @param {string} props.primaryAction.label - Label for the primary action button.
 * @param {Function} props.primaryAction.action - Callback function for the primary action.
 * @param {Object} props.secondaryAction - Secondary action for the modal.
 * @param {string} props.secondaryAction.label - Label for the secondary action button.
 * @param {Function} props.secondaryAction.action - Callback function for the secondary action.
 */
export const GeneralModalNew = ({
  open,
  handleClose,
  title,
  content,
  primaryAction,
  secondaryAction,
}) => {
  return (
    <Dialog open={open} onClose={handleClose}>
      {title && <DialogTitle>{title}</DialogTitle>}
      <DialogContent>
        <Typography variant='body1' style={{ whiteSpace: 'pre-line' }} paragraph>
          {content}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          variant='outlined'
          onClick={secondaryAction.action}
          color='primary'
          sx={{
            width: '50%',
          }}
        >
          {secondaryAction.label}
        </Button>
        <Button
          onClick={primaryAction.action}
          color='primary'
          autoFocus
          sx={{
            width: '50%',
          }}
        >
          {primaryAction.label}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

GeneralModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  title: PropTypes.string,
  content: PropTypes.string,
  primaryAction: PropTypes.shape({
    label: PropTypes.string,
    action: PropTypes.func,
  }),
  secondaryAction: PropTypes.shape({
    label: PropTypes.string,
    action: PropTypes.func,
  }),
}

export default GeneralModal
